import { useEffect, useState } from 'react';
import api from '../../services/api';

const ORIGIN = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL);

const LOGO_SIZE_CLASSES = {
  small: 'h-10',
  medium: 'h-14',
  large: 'h-20',
  xlarge: 'h-24',
};

const LOGO_ALIGNMENT_CLASSES = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

// iter309-security PR1: on platform hosts (my.vyomos.io etc) the
// tenant-scoped /admin/logo endpoint 400s because middleware rejects
// unresolved-tenant requests. Detect the platform host client-side
// and hit /platform/branding instead so we never bleed another
// tenant's asset into the platform shell.
const PLATFORM_HOSTS = new Set(
  (process.env.REACT_APP_PLATFORM_HOSTS || 'my.vyomos.io,vyomos.io,localhost,127.0.0.1')
    .split(',').map(s => s.trim().toLowerCase()).filter(Boolean),
);

function _isPlatformHost() {
  if (typeof window === 'undefined' || !window.location) return false;
  return PLATFORM_HOSTS.has(window.location.hostname.toLowerCase());
}

/**
 * Fetch and cache the business logo + size + alignment in localStorage so the
 * sidebar doesn't flash the fallback text on every page load.
 *
 * Uses the authenticated `api` instance (not plain fetch) so the Bearer JWT's
 * `tenant_id` claim is honored by TenantMiddleware. On platform hosts we hit
 * /platform/branding (neutral Vyomos asset) instead of the tenant-scoped
 * /admin/logo endpoint.
 */
export function useBusinessLogo() {
  const [logoUrl, setLogoUrl] = useState(() => localStorage.getItem('businessLogoUrl') || null);
  const [logoSize, setLogoSize] = useState(() => localStorage.getItem('businessLogoSize') || 'medium');
  const [logoAlignment, setLogoAlignment] = useState(
    () => localStorage.getItem('businessLogoAlignment') || 'left',
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (_isPlatformHost()) {
          const res = await api.get('/platform/branding');
          if (cancelled) return;
          const data = res.data || {};
          const url = data.logo_url || null;
          setLogoUrl(url);
          if (url) localStorage.setItem('businessLogoUrl', url);
          else localStorage.removeItem('businessLogoUrl');
          setLogoSize('medium');
          setLogoAlignment('left');
          return;
        }
        const res = await api.get('/admin/logo');
        if (cancelled) return;
        const data = res.data || {};
        if (data.logo_url) {
          setLogoUrl(data.logo_url);
          localStorage.setItem('businessLogoUrl', data.logo_url);
        } else {
          setLogoUrl(null);
          localStorage.removeItem('businessLogoUrl');
        }
        if (data.logo_size) {
          setLogoSize(data.logo_size);
          localStorage.setItem('businessLogoSize', data.logo_size);
        }
        if (data.logo_alignment) {
          setLogoAlignment(data.logo_alignment);
          localStorage.setItem('businessLogoAlignment', data.logo_alignment);
        }
      } catch {
        // keep cached value
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    logoUrl: logoUrl ? `${ORIGIN}${logoUrl}` : null,
    sizeClass: LOGO_SIZE_CLASSES[logoSize] || 'h-14',
    alignmentClass: LOGO_ALIGNMENT_CLASSES[logoAlignment] || 'justify-start',
  };
}
